import { makeAutoObservable, runInAction, reaction, type IReactionDisposer } from 'mobx'
import { AxiosError } from 'axios'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { uiStore } from 'shared/store/uiStore'
import { logger } from 'shared/lib'
import { IntegrationsApi } from 'entities/Integrations'
import { ConnectTwilioModalActions } from 'entities/Phone/ui/ConnectTwilioModal/ui/ConnectTwilioModalActions'
import { ConnectTwilioModalContent } from 'entities/Phone/ui/ConnectTwilioModal/ui/ConnectTwilioModalContent'
import type { IEntitiesStore } from 'entities/EntitiesExport'

export class ConnectTwilioModalStore {
  private _idModal = 'connectTwilioModal'
  private _accountSid = ''
  private _isPrefilled = false
  private _authToken = ''
  private _apiKeySid = ''
  private _apiKeySecret = ''
  private _error: string | null = null
  private _loading = false
  private _disposeInit: IReactionDisposer | null = null
  private _disposeChange: IReactionDisposer | null = null

  constructor(private _entities: IEntitiesStore, private _redirectToNumbersPage: boolean = true) {
    makeAutoObservable(this)
  }

  get isPrefilled() {
    return this._isPrefilled
  }

  get error() {
    return this._error
  }

  get accountSid() {
    return this._accountSid
  }

  get authToken() {
    return this._authToken
  }

  get apiKeySid() {
    return this._apiKeySid
  }

  get apiKeySecret() {
    return this._apiKeySecret
  }

  get isApiKeyCredentials() {
    return this._entities.featureFlagsStore.twilio_integration_api_key_credentials
  }

  get loading() {
    return this._loading
  }

  get hasError() {
    return Boolean(this._error)
  }

  get disabled() {
    if (this.isApiKeyCredentials) {
      return !this._apiKeySid || !this._apiKeySecret || this.hasError
    }

    return !this._accountSid || !this._authToken || this.hasError
  }

  setAccountSid = (accountSid: string) => {
    this._accountSid = accountSid
  }

  setAuthToken = (authToken: string) => {
    this._authToken = authToken
  }

  setApiKeySid = (apiKeySid: string) => {
    this._apiKeySid = apiKeySid
  }

  setApiKeySecret = (apiKeySecret: string) => {
    this._apiKeySecret = apiKeySecret
  }

  init = async () => {
    this.reactionInit()
    this.reactionChange()

    const accountSid = new URLSearchParams(window.location.search).get('accountSid')
    if (accountSid) {
      this._accountSid = accountSid
      this._isPrefilled = true
    }

    await Promise.all([
      this._entities.usersStore.initPromise,
      this._entities.organizationStore.initPromise,
      this._entities.numbersStore.initFetchOrganizationNumberPromise,
      this._entities.numbersStore.initFetchNumberVendorIntegrationsPromise,
      this._entities.featureFlagsStore.fetchFeatureFlags(),
    ])

    if (
      this._entities.organizationStore.isTwilioNumberVendor &&
      !this._entities.numbersStore.hasTwilioNumberVendorIntegration
    ) {
      modalStore.addModal({
        id: this._idModal,
        showCloseIcon: false,
        showCloseButton: false,
        disabledOnAllClose: true,
        disabledOverlayClose: true,
        disabledOnEscClose: true,
        title: 'Twilio integration',
        paddingContent: '8px 24px 0 24px',
        width: 480,
        ModalContentProps: {
          store: this,
        },
        ModalActions: ConnectTwilioModalActions,
        ModalContent: ConnectTwilioModalContent,
        onClose: this.closeModal,
      })
    } else {
      this.reset()
      this.closeModal()
    }
  }

  closeModal = () => {
    this.reset()
    modalStore.removeModal(this._idModal)
  }

  reset = () => {
    this._authToken = ''
    this._apiKeySid = ''
    this._apiKeySecret = ''
    this._accountSid = ''
    this._error = null
    this._loading = false
    this._disposeInit?.()
    this._disposeChange?.()
  }

  handleSubmit = async () => {
    try {
      runInAction(() => {
        this._loading = true
      })

      await IntegrationsApi.connectTwilio(
        this.isApiKeyCredentials
          ? { api_key: this._apiKeySid, api_key_secret: this._apiKeySecret }
          : { account_sid: this._accountSid, auth_token: this._authToken }
      )

      await window.publicApiVue?.fetchMe()
      await Promise.all([
        this._entities.organizationStore.init(),
        this._entities.numbersStore.fetchNumberVendorIntegrations(),
      ])

      this.closeModal()

      if (this._redirectToNumbersPage) {
        uiStore.changeRoute({
          path: '/settings/organization/numbers',
        })
      }
    } catch (e) {
      logger.error(e)
      if (e instanceof AxiosError) {
        this._error = e.errors.getError('error') || 'Something went wrong, please try again.'
      }
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  reactionInit = () => {
    this._disposeInit?.()
    this._disposeInit = reaction(
      () => [
        this._entities.organizationStore.isTwilioNumberVendor,
        this._entities.numbersStore.hasTwilioNumberVendorIntegration,
        this._entities.numbersStore.loading,
        this._entities.organizationStore.loading,
      ],
      () => {
        this.init()
      }
    )
  }

  reactionChange = () => {
    this._disposeChange?.()
    this._disposeChange = reaction(
      () => [this._accountSid, this._authToken, this._apiKeySid, this._apiKeySecret],
      () => {
        this._error = null
      }
    )
  }
}
